import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CountdownFlipClock, Group, Modal, Stack, Text } from '@wapharma/design-system';
import { refreshAccessToken, safeReturnPathFromLocation } from '../api';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getJwtExpiryMs } from '../utils/jwtExpiry';

/** Show modal when access token expires within this window. */
const WARNING_LEAD_MS = 5 * 60 * 1000;
const TICK_MS = 1000;
const IDLE_TICK_MS = 30_000;
/** Alert only after continuous inactivity period. */
const INTERACTIVITY_IDLE_DELAY_MS = 45 * 1000;
/** After « Ignorer », hide again briefly (still warns before hard expiry via API). */
const SNOOZE_MS = 90 * 1000;
/** While user is active near/after expiry, try silent refresh periodically. */
const ACTIVE_REFRESH_COOLDOWN_MS = 60 * 1000;

/**
 * Modal + countdown before access JWT expiry; « Rester connecté » calls refresh endpoint.
 * Render under authenticated shell only.
 */
export function SessionExpiryWarning() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const [pending, setPending] = useState(false);
  const snoozeUntilRef = useRef(0);
  const lastInteractionAtRef = useRef(Date.now());
  const activeRefreshPendingRef = useRef(false);
  const activeRefreshNextAtRef = useRef(0);
  /** After access JWT exp passed — run logout+redirect once until new tokens arrive. */
  const expiryHandledRef = useRef(false);

  const closeAndSnooze = useCallback(() => {
    setOpen(false);
    snoozeUntilRef.current = Date.now() + SNOOZE_MS;
  }, []);

  useEffect(() => {
    const onRefreshed = () => {
      setOpen(false);
      snoozeUntilRef.current = 0;
      activeRefreshPendingRef.current = false;
      activeRefreshNextAtRef.current = 0;
      expiryHandledRef.current = false;
    };
    window.addEventListener('wapharma:token-refreshed', onRefreshed);
    return () => window.removeEventListener('wapharma:token-refreshed', onRefreshed);
  }, []);

  useEffect(() => {
    const markInteraction = () => {
      lastInteractionAtRef.current = Date.now();
    };
    const onVisibility = () => {
      if (!document.hidden) markInteraction();
    };
    const opts: AddEventListenerOptions = { passive: true };
    markInteraction();
    window.addEventListener('pointerdown', markInteraction, opts);
    window.addEventListener('keydown', markInteraction);
    window.addEventListener('wheel', markInteraction, opts);
    window.addEventListener('scroll', markInteraction, opts);
    window.addEventListener('touchstart', markInteraction, opts);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pointerdown', markInteraction, opts);
      window.removeEventListener('keydown', markInteraction);
      window.removeEventListener('wheel', markInteraction, opts);
      window.removeEventListener('scroll', markInteraction, opts);
      window.removeEventListener('touchstart', markInteraction, opts);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    let timeoutId: number | undefined;

    const tick = () => {
      const t = getToken();
      if (!t) {
        setOpen(false);
        return TICK_MS;
      }
      const expMs = getJwtExpiryMs(t);
      if (expMs == null) {
        setOpen(false);
        return IDLE_TICK_MS;
      }
      const now = Date.now();
      const rem = expMs - now;
      const idleForMs = now - lastInteractionAtRef.current;
      setRemainingMs(rem);
      if (rem <= 0) {
        if (idleForMs < INTERACTIVITY_IDLE_DELAY_MS) {
          setOpen(false);
          setRemainingMs(0);
          if (!activeRefreshPendingRef.current && now >= activeRefreshNextAtRef.current) {
            activeRefreshPendingRef.current = true;
            activeRefreshNextAtRef.current = now + ACTIVE_REFRESH_COOLDOWN_MS;
            void refreshAccessToken().finally(() => {
              activeRefreshPendingRef.current = false;
            });
          }
          return TICK_MS;
        }
        setOpen(false);
        setRemainingMs(0);
        if (!expiryHandledRef.current) {
          expiryHandledRef.current = true;
          showToast('Session expirée. Redirection vers la connexion…', { tone: 'info', autoDismissMs: 2800 });
          void (async () => {
            await logout();
            const next = safeReturnPathFromLocation();
            window.location.assign(`/login?next=${encodeURIComponent(next)}`);
          })();
        }
        return TICK_MS;
      }
      expiryHandledRef.current = false;
      if (rem > WARNING_LEAD_MS) {
        setOpen(false);
        return Math.min(IDLE_TICK_MS, Math.max(TICK_MS, rem - WARNING_LEAD_MS));
      }
      if (idleForMs < INTERACTIVITY_IDLE_DELAY_MS) {
        setOpen(false);
        if (!activeRefreshPendingRef.current && now >= activeRefreshNextAtRef.current) {
          activeRefreshPendingRef.current = true;
          activeRefreshNextAtRef.current = now + ACTIVE_REFRESH_COOLDOWN_MS;
          void refreshAccessToken().finally(() => {
            activeRefreshPendingRef.current = false;
          });
        }
        return TICK_MS;
      }
      if (now < snoozeUntilRef.current) {
        setOpen(false);
        return TICK_MS;
      }
      setOpen(true);
      return TICK_MS;
    };

    const scheduleNext = () => {
      const delay = tick();
      timeoutId = window.setTimeout(scheduleNext, delay);
    };

    scheduleNext();
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [logout, showToast]);

  const onStay = async () => {
    setPending(true);
    try {
      const ok = await refreshAccessToken();
      if (ok) {
        showToast('Session prolongée.', { tone: 'success' });
        setOpen(false);
        snoozeUntilRef.current = 0;
      } else {
        showToast('Impossible de prolonger la session. Reconnectez-vous bientôt.', { tone: 'error' });
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal opened={open} onClose={closeAndSnooze} title="Session bientôt expirée">
      <Stack gap="md">
        <Text size="sm">
          Vous serez déconnecté(e) dans&nbsp;:
        </Text>
        <CountdownFlipClock totalMs={remainingMs} />
        <Group justify="flex-end" gap="sm" wrap="wrap">
          <Button type="button" variant="ghost" onClick={closeAndSnooze} disabled={pending}>
            Ignorer
          </Button>
          <Button type="button" variant="primary" onClick={() => void onStay()} disabled={pending}>
            {pending ? 'Prolongation…' : 'Rester connecté(e)'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
